import type { ApiResponse, PipelineRun, PipelineDashboard, ProjectPipelineAnalytics, WebhookEvent, RunbookExecution, RunbookInfo, RunbookHealthReport, WorkItemGuardAction, BugSourceResolution, SprintBoard, SprintItem, WorkItemDraft, CreateWorkItemRequest, TriggerAdoRequest, TriggerGitHubRequest, TeamHoursReport, CostReport, ReleaseDashboard, OrgComplianceReport, StatusCheckCandidates, FixResult, VersionMapReport, NetworkTopologyReport, EfMigrationCheckRecord, EfMigrationCheckResult, ApiInvocation } from './types'
import { getApiBase } from '../apiConfig'
import { getToken } from '../auth'

export const AUTH_REQUIRED_EVENT = 'devops-api:auth-required'

function apiKey(): string {
  return localStorage.getItem('devops-api-key') ?? ''
}

async function request<T>(path: string, init?: RequestInit): Promise<ApiResponse<T>> {
  const token = getToken()
  const res = await fetch(`${getApiBase()}/api${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Api-Key': apiKey(),
      // The session cookie itself can't be relied on here — browsers treat
      // it as a blocked third-party cookie once embedded in another site's
      // iframe. This token (minted via /api/auth/token right after a real
      // login) is what BearerTokenFilter actually checks on the backend.
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  })
  if (res.status === 401) {
    window.dispatchEvent(new Event(AUTH_REQUIRED_EVENT))
    throw new Error('Not signed in')
  }
  if (!res.ok && res.status === 204) return { success: true } as ApiResponse<T>
  return res.json()
}

// Cheap, side-effect-free way to know up front whether the session is valid,
// so the app can gate its whole UI behind a sign-in screen instead of briefly
// showing the dashboard chrome before a real data call fails.
export async function checkAuth(): Promise<boolean> {
  const token = getToken()
  if (!token) return false
  try {
    const res = await fetch(`${getApiBase()}/api/auth/verify`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return res.ok
  } catch {
    return false
  }
}

// ── pipelines ────────────────────────────────────────────────────────────────

export const getPipelineRuns = (limit = 20) =>
  request<PipelineRun[]>(`/pipelines?limit=${limit}`)

export const getPipelineDashboard = () =>
  request<PipelineDashboard>('/pipelines/dashboard')

export const getProjectPipelineAnalytics = (project: string, top = 300) =>
  request<ProjectPipelineAnalytics>(`/pipelines/analytics/${encodeURIComponent(project)}?top=${top}`)

export const triggerAdoPipeline = (body: TriggerAdoRequest) =>
  request<{ runId: string; status: string; runUrl?: string }>('/pipelines/trigger/ado', {
    method: 'POST',
    body: JSON.stringify(body),
  })

export const triggerGitHubWorkflow = (body: TriggerGitHubRequest) =>
  request<{ runId: string; status: string; runUrl?: string }>('/pipelines/trigger/github', {
    method: 'POST',
    body: JSON.stringify(body),
  })

// ── webhooks ─────────────────────────────────────────────────────────────────

export const getWebhookEvents = (limit = 20) =>
  request<WebhookEvent[]>(`/webhooks?limit=${limit}`)

// ── runbooks ─────────────────────────────────────────────────────────────────

export const getRunbooks = () =>
  request<RunbookInfo[]>('/runbooks')

export const getRunbookHealth = () =>
  request<RunbookHealthReport>('/runbooks/health')

export const getRunbookExecutions = (limit = 20) =>
  request<RunbookExecution[]>(`/runbooks/executions?limit=${limit}`)

export const triggerRunbook = (name: string, parameters?: Record<string, string>) =>
  request<{ executionId: string; status: string }>(`/runbooks/${encodeURIComponent(name)}/trigger`, {
    method: 'POST',
    body: JSON.stringify({ parameters }),
  })

// ── work item guard ───────────────────────────────────────────────────────────

export const getGuardLog = (limit = 50) =>
  request<WorkItemGuardAction[]>(`/workitems/guard-log?limit=${limit}`)

export const getBugSourceLog = (limit = 50) =>
  request<BugSourceResolution[]>(`/workitems/bug-source-log?limit=${limit}`)

// ── EF migration PR safety check ────────────────────────────────────────────

export const getEfMigrationChecks = (limit = 50) =>
  request<EfMigrationCheckRecord[]>(`/ef-migration-pr-safety/checks?limit=${limit}`)

export const triggerEfMigrationCheck = (owner: string, repo: string, pullNumber: number) =>
  request<EfMigrationCheckResult>('/ef-migration-pr-safety/check', {
    method: 'POST',
    body: JSON.stringify({ owner, repo, pullNumber }),
  })

// ── API invocations ─────────────────────────────────────────────────────────

export const getApiInvocations = (limit = 50) =>
  request<ApiInvocation[]>(`/invocations?limit=${limit}`)

// ── scrum ─────────────────────────────────────────────────────────────────────

export const getSprint = (project?: string, team?: string) => {
  const params = new URLSearchParams()
  if (project) params.set('project', project)
  if (team) params.set('team', team)
  const qs = params.size ? `?${params}` : ''
  return request<SprintBoard>(`/scrum/sprint${qs}`)
}

export const draftWorkItem = (description: string, workItemType?: string) =>
  request<WorkItemDraft>('/scrum/draft', {
    method: 'POST',
    body: JSON.stringify({ description, workItemType }),
  })

export const createWorkItem = (body: CreateWorkItemRequest) =>
  request<SprintItem>('/scrum/create', {
    method: 'POST',
    body: JSON.stringify(body),
  })

export const getTeamHours = (project?: string, team?: string, year?: number, month?: number) => {
  const params = new URLSearchParams()
  if (project) params.set('project', project)
  if (team)    params.set('team', team)
  if (year  != null) params.set('year',  String(year))
  if (month != null) params.set('month', String(month))
  const qs = params.size ? `?${params}` : ''
  return request<TeamHoursReport>(`/scrum/team-hours${qs}`)
}

// ── releases ──────────────────────────────────────────────────────────────────

export const getReleaseDashboard = () =>
  request<ReleaseDashboard>('/releases/dashboard')

// ── version map ───────────────────────────────────────────────────────────────

export const getVersionMap = () =>
  request<VersionMapReport>('/version-map')

export const refreshVersionMap = () =>
  request<VersionMapReport>('/version-map/refresh', { method: 'POST' })

export const getNetworkTopology = () =>
  request<NetworkTopologyReport>('/network-topology')

// ── cost ──────────────────────────────────────────────────────────────────────

export const getMonthlyCost = (year?: number, month?: number, resourceGroups?: string[]) => {
  const params = new URLSearchParams()
  if (year  != null) params.set('year',  String(year))
  if (month != null) params.set('month', String(month))
  if (resourceGroups?.length) params.set('resourceGroups', resourceGroups.join(','))
  const qs = params.size ? `?${params}` : ''
  return request<CostReport>(`/cost/monthly${qs}`)
}

// ── org compliance ────────────────────────────────────────────────────────────

export const getComplianceReport = () =>
  request<OrgComplianceReport>('/compliance')

export const refreshComplianceReport = () =>
  request<OrgComplianceReport>('/compliance/refresh', { method: 'POST' })

export const setRepoExcluded = (repoFullName: string, excluded: boolean, reason?: string) =>
  request<{ updated: boolean }>('/compliance/exclude', {
    method: 'POST',
    body: JSON.stringify({ repoFullName, excluded, reason: reason ?? null }),
  })

export const enableCodeQl = (owner: string, repo: string) =>
  request<FixResult>(`/compliance/${owner}/${repo}/fix/codeql`, { method: 'POST' })

export const createUnitTestsFixPr = (owner: string, repo: string) =>
  request<{ prUrl: string | null; message: string | null }>(`/compliance/${owner}/${repo}/fix/unit-tests`, {
    method: 'POST',
  })

export const getStatusCheckCandidates = (owner: string, repo: string) =>
  request<StatusCheckCandidates>(`/compliance/${owner}/${repo}/status-checks/candidates`)

export const applyStatusChecks = (owner: string, repo: string, contexts: string[]) =>
  request<FixResult>(`/compliance/${owner}/${repo}/status-checks/apply`, {
    method: 'POST',
    body: JSON.stringify({ contexts }),
  })
